import { errWithCause } from "pino-std-serializers";
import { configure } from "safe-stable-stringify";

// Customer PII keys, matched case-insensitively at any depth. The server-side relay only redacts
// two levels deep, and these turn up nested in whole Task / callback / notification payloads.
const REDACTED_KEYS = new Set([
    "customername",
    "customeremailid",
    "customermdn",
    "customerchatid",
    "email",
    "mdn",
    "callbackmdn",
    "callerid",
    "phonenumber",
]);

export const REDACTED = "[Redacted]";

const hasStringBody = (value: unknown): value is { body: string } =>
    typeof value === "object" && value !== null && typeof (value as { body?: unknown }).body === "string";

export function jsonReplacer(key: string, value: unknown) {
    // Twilio data has a LOT of private members that may hold sensitive information like our auth token
    // These are all prefixed with an underscore, so we can filter them out here
    if (key.startsWith("_")) return undefined;

    if (REDACTED_KEYS.has(key.toLowerCase()) && value !== undefined && value !== null) return REDACTED;

    // A ky HTTPError keeps the request body on `options` as a string, which the key match above can't see into.
    if (key === "options" && hasStringBody(value)) return { ...value, body: REDACTED };

    // https://github.com/pinojs/pino/issues/2132
    if (value instanceof Error) return errWithCause(value);

    return value;
}

const stringify = configure({ deterministic: false, maximumBreadth: 30, maximumDepth: 10, strict: false });

/**
 * Serializes a value through {@link jsonReplacer} into a single string. For payloads whose shape varies
 * per writer, a string keeps the field one type so it cannot collide, at the cost of its contents
 * not being individually searchable.
 */
export function stringifyLogValue(value: unknown): string {
    return stringify(value, jsonReplacer) ?? "null";
}
